import {
  DEFAULT_QUANTITY_EXPRESSION,
  DEFAULT_QUANTITY_EXPRESSIONS,
  ElementQuantityRecord,
  ElementQuantityExpressionRecord,
  IElementQuantityExpressionProperty,
  IElementQuantityProperty,
  ElementQuantityName,
  ElementQuantityExpressionName,
  dimensionQuantityNames,
  areaQuantityNames,
  DimensionQuantityName,
} from '../models/element-quantities.model';
import {
  ExpressionValue,
  IElement,
  OneOfParentElements,
} from '../models/project.model';
import shallowEqual, {
  cloneRemoveItems,
  includesAll,
  isDefined,
} from './array.helpers';
import {
  getElementProperties,
  getCount,
  getElementPropertyResolvedCount,
  hasCount,
  hasFallbackCount,
  isInheritanceSupported,
} from './element-property.helpers';
import { isExpressionValue } from './expression-factory.helpers';
import {
  createElementQuantityRecord,
  getDefaultFallbackExpressionByName,
  getElementQuantityExpressionRecord,
  getElementQuantityRecord,
  getInvalidSelectSwitchFallbackClears,
  hasExplicitQuantityCount,
  isElementQuantityExpressionName,
  isElementQuantityExpressionProperty,
  isElementQuantityName,
} from './element-quantity.helpers';
import { applyChanges, getKeys } from './object.helpers';
import { createExpression } from './expression-factory.helpers';
import { getElementCategory } from './element-category.helpers';
import {
  ExpressionVariables,
  ExpressionVariablesRecord,
  defaultVariables,
  hasCircularDependencies,
} from './expression-variables.helpers';
import { countOccurrences } from './string.helpers';
import { getVariablesInExpression } from './mathjs';
import { errorHandledSolve } from './expression-solving.helpers';
import { isNumeric } from './math.helpers';
import { cacheFactory } from './function.helpers';
import { guards } from './typeguards.helpers';

/** Cache quantity record per category so we only run createElementQuantityRecord once per category */
const defaultQuantityRecordByCategoryId = new Map<
  string,
  ElementQuantityRecord
>();

/**
 * These properties can be used in fallback even though they are not set by user
 */
const ALLOW_FALLBACK_REFERENCES: ElementQuantityName[] = [
  'fill',
  'density',
  'density_areal_side',
  'density_areal_top',
];

/**
 * These properties can't inherit properties from parent in it's fallback
 */
const FORBIDDEN_INHERITANCE: ElementQuantityName[] = [
  'fill',
  'density',
  'density_areal_side',
  'density_areal_top',
];

/** Areal density (kg/m²) parts — only contribute to mass when no volumetric density wins. */
const AREAL_DENSITY_PARTS: ElementQuantityName[] = [
  'density_areal_side',
  'density_areal_top',
];

const AUTO_QUANTITY_EXCLUDED_NAMES = new Set<string>(['pieces']);
const GET_PARENT_VALUE_FN_NAME = 'getParentValue';
const INHERITABLE_QUANTITY_NAMES: (keyof ElementQuantityExpressionRecord)[] = [
  // 'repeating_item_spacing',
  // 'repeating_item_thickness',
  // 'repeating_item_count',
  'length',
  'width',
  'height',
  'area_side',
  'area_top',
  'area_section',
  'energy',
];

const INHERITABLE_QUANTITY_NAME_SET = new Set<string>(
  INHERITABLE_QUANTITY_NAMES,
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const operators = [
  '+',
  '-',
  '*',
  '/',
  '(',
  ')',
  ',',
  '100',
  '1',
  'sqrt(',
  'firstNonZero(',
] as const;
type Operator = (typeof operators)[number];
type FormulaPart = keyof ElementQuantityExpressionRecord | Operator;
type Formula = FormulaPart[];
type Replacements = Record<keyof ElementQuantityExpressionRecord, Formula>;

export interface IAutoDependency {
  name: keyof ElementQuantityExpressionRecord;
  formula: Formula;
}

/**
 * The different auto dependencies for volume
 */
const volumeAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  {
    name: 'volume',
    formula: ['length', '*', 'width', '*', 'height'],
  },
  {
    name: 'volume',
    formula: ['area_side', '*', 'width'],
  },
  {
    name: 'volume',
    formula: ['area_side', '*', 'area_top', '/', 'length'],
  },
  {
    name: 'volume',
    formula: ['area_side', '*', 'area_section', '/', 'height'],
  },
  {
    name: 'volume',
    formula: ['area_top', '*', 'height'],
  },
  {
    name: 'volume',
    formula: ['area_top', '*', 'area_section', '/', 'width'],
  },
  {
    name: 'volume',
    formula: ['area_section', '*', 'length'],
  },
  {
    name: 'volume',
    formula: ['(', ...replacements.mass, '/', 'density', ')', '/', 'fill'],
  },
  {
    name: 'volume',
    formula: ['sqrt(', 'area_side', '*', 'area_top', '*', 'area_section', ')'],
  },
];

/**
 * Get all possible dependendies for a property in relation to volume
 * (almost everything can be related to volume)
 * @param name Which property that will be related to volume
 * @param baseFormula Formula to prefix
 * @param prefix If true the volume formula will prefix the given baseFormula
 * @returns
 */
const getVolumeDependencies = (
  replacements: Replacements,
  name: keyof ElementQuantityExpressionRecord,
  baseFormula: Formula,
  prefix = true,
): IAutoDependency[] => {
  const volumeDep: IAutoDependency = {
    name,
    formula: prefix ? ['volume', ...baseFormula] : [...baseFormula, 'volume'],
  };

  const deps: IAutoDependency[] = volumeAutoDependencies(replacements).map(
    (dependency) => ({
      ...dependency,
      formula: prefix
        ? ['(', ...dependency.formula, ')', ...baseFormula]
        : [...baseFormula, '(', ...dependency.formula, ')'],
      name,
    }),
  );

  return [volumeDep, ...deps]
    .filter((dependency) => !dependency.formula.includes(name))
    .map((dependency) => ({
      ...dependency,
    }));
};

const areaSideAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  {
    name: 'area_side',
    formula: ['length', '*', 'height'],
  },
  {
    name: 'area_side',
    formula: ['height', '*', 'area_top', '/', 'width'],
  },
  {
    name: 'area_side',
    formula: [
      '(',
      'area_top',
      '/',
      'width',
      ')',
      '*',
      '(',
      'area_section',
      '/',
      'width',
      ')',
    ],
  },
  ...getVolumeDependencies(replacements, 'area_side', ['/', 'width']),
  ...getVolumeDependencies(replacements, 'area_side', [
    '/',
    '(',
    'area_top',
    '/',
    'length',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'area_side', [
    '/',
    '(',
    'area_section',
    '/',
    'height',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'area_side', [
    '*',
    'volume',
    '/',
    'area_top',
    '/',
    'area_section',
  ]),
  {
    name: 'area_side',
    formula: [...replacements.mass, '/', 'density_areal_side', '/', 'fill'],
  },
  {
    name: 'area_top',
    formula: [...replacements.mass, '/', 'density_areal_top', '/', 'fill'],
  },
];

const areaTopAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  {
    name: 'area_top',
    formula: ['length', '*', 'width'],
  },
  {
    name: 'area_top',
    formula: ['width', '*', 'area_side', '/', 'height'],
  },
  {
    name: 'area_top',
    formula: [
      '(',
      'area_side',
      '/',
      'height',
      ')',
      '*',
      '(',
      'area_section',
      '/',
      'height',
      ')',
    ],
  },
  ...getVolumeDependencies(replacements, 'area_top', ['/', 'height']),
  ...getVolumeDependencies(replacements, 'area_top', [
    '/',
    '(',
    'area_side',
    '/',
    'length',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'area_top', [
    '/',
    '(',
    'area_section',
    '/',
    'width',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'area_top', [
    '*',
    'volume',
    '/',
    'area_side',
    '/',
    'area_section',
  ]),
];

const areaSectionAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  {
    name: 'area_section',
    formula: ['width', '*', 'height'],
  },
  {
    name: 'area_section',
    formula: ['height', '*', 'area_top', '/', 'length'],
  },
  {
    name: 'area_section',
    formula: [
      '(',
      'area_top',
      '/',
      'length',
      ')',
      '*',
      '(',
      'area_side',
      '/',
      'length',
      ')',
    ],
  },
  ...getVolumeDependencies(replacements, 'area_section', ['/', 'length']),
  ...getVolumeDependencies(replacements, 'area_section', [
    '/',
    '(',
    'area_top',
    '/',
    'width',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'area_section', [
    '/',
    '(',
    'area_side',
    '/',
    'height',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'area_section', [
    '*',
    'volume',
    '/',
    'area_top',
    '/',
    'area_side',
  ]),
];

const massAutoDependencies = (
  quantity: ElementQuantityRecord,
  replacements: Replacements,
): IAutoDependency[] => {
  const repeatingFormula: Formula | undefined =
    getMassRepeatingItemsReplacementCondition(quantity)
      ? ['repeating_item_thickness', '/', 'repeating_item_spacing', '*']
      : [];

  const formulas: IAutoDependency[] = [
    ...getVolumeDependencies(replacements, 'mass', [
      '*',
      'density',
      '*',
      'fill',
    ]),
    ...getVolumeDependencies(replacements, 'mass', [
      '/',
      'width',
      '*',
      'density_areal_side',
      '*',
      'fill',
    ]),
    ...getVolumeDependencies(replacements, 'mass', [
      '/',
      'height',
      '*',
      'density_areal_top',
      '*',
      'fill',
    ]),
    {
      name: 'mass',
      formula: ['density_areal_side', '*', 'area_side', '*', 'fill'],
    },
    {
      name: 'mass',
      formula: ['density_areal_side', '*', 'length', '*', 'height'],
    },
    {
      name: 'mass',
      formula: ['density_areal_top', '*', 'area_top', '*', 'fill'],
    },
    {
      name: 'mass',
      formula: ['density_areal_top', '*', 'length', '*', 'width'],
    },
  ];

  return repeatingFormula
    ? formulas.map(({ name, formula }) => ({
        name,
        formula: [...repeatingFormula, ...formula],
      }))
    : formulas;
};

const lengthAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  {
    name: 'length',
    formula: ['area_top', '/', 'width'],
  },
  {
    name: 'length',
    formula: ['area_side', '/', 'height'],
  },
  ...getVolumeDependencies(replacements, 'length', [
    '/',
    '(',
    'width',
    '*',
    'height',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'length', [
    '/',
    'volume',
    '/',
    '(',
    'volume',
    '/',
    '(',
    'area_side',
    '*',
    'area_top',
    ')',
    ')',
  ]),
  ...getVolumeDependencies(replacements, 'length', ['/', 'area_section']),
];

/**
 * Resolved numeric value for a repeating quantity row (`count.value` wins over `count.resolved`).
 */
const getRepeatingQuantityResolved = (
  row?: IElementQuantityExpressionProperty,
): number | undefined => {
  if (!row) return undefined;
  if (hasCount(row)) return getElementPropertyResolvedCount(row, 0);
  if (hasFallbackCount(row) && isExpressionValue(row.fallbackCount)) {
    return row.fallbackCount.resolved;
  }
  return undefined;
};

const hasNonZeroRepeatingQuantity = (
  row?: IElementQuantityExpressionProperty,
): boolean => (getRepeatingQuantityResolved(row) ?? 0) !== 0;

/**
 * Sum used for horizontal width / vertical height repeating dimension formulas.
 */
const getRepeatingDimensionFormulaResolved = (
  quantity: ElementQuantityRecord,
): number => {
  const spacing =
    getRepeatingQuantityResolved(quantity.repeating_item_spacing) ?? 0;
  const count =
    getRepeatingQuantityResolved(quantity.repeating_item_count) ?? 0;
  const thickness =
    getRepeatingQuantityResolved(quantity.repeating_item_thickness) ?? 0;
  return spacing * (count - 1) + thickness;
};

const widthAutoDependencies = (
  quantity: ElementQuantityRecord,
  replacements: Replacements,
): IAutoDependency[] => {
  const repeatingFormulaResolved =
    getRepeatingDimensionFormulaResolved(quantity);
  return [
    {
      name: 'width',
      formula: ['area_section', '/', 'height'],
    },
    {
      name: 'width',
      formula: ['area_top', '/', 'length'],
    },
    ...getVolumeDependencies(replacements, 'width', [
      '/',
      '(',
      'length',
      '*',
      'height',
      ')',
    ]),
    ...getVolumeDependencies(replacements, 'width', [
      '/',
      'volume',
      '/',
      '(',
      'volume',
      '/',
      '(',
      'area_top',
      '*',
      'area_section',
      ')',
      ')',
    ]),
    ...getVolumeDependencies(replacements, 'width', ['/', 'area_side']),
    ...(quantity.repeating_items?.count &&
    quantity.repeating_direction?.count === 'horizontal' &&
    repeatingFormulaResolved > 0
      ? [
          {
            name: 'width',
            formula: [
              'repeating_item_spacing',
              '*',
              '(',
              'repeating_item_count',
              '-',
              '1',
              ')',
              '+',
              'repeating_item_thickness',
            ],
          } as IAutoDependency,
        ]
      : []),
  ];
};

const heightAutoDependencies = (
  quantity: ElementQuantityRecord,
  replacements: Replacements,
): IAutoDependency[] => {
  const repeatingFormulaResolved =
    getRepeatingDimensionFormulaResolved(quantity);

  return [
    {
      name: 'height',
      formula: ['area_side', '/', 'length'],
    },
    {
      name: 'height',
      formula: ['area_section', '/', 'width'],
    },
    ...getVolumeDependencies(replacements, 'height', [
      '/',
      '(',
      'length',
      '*',
      'width',
      ')',
    ]),
    ...getVolumeDependencies(replacements, 'height', [
      '/',
      'volume',
      '/',
      '(',
      'volume',
      '/',
      '(',
      'area_side',
      '*',
      'area_section',
      ')',
      ')',
    ]),
    ...getVolumeDependencies(replacements, 'height', ['/', 'area_top']),
    ...(quantity.repeating_items?.count &&
    quantity.repeating_direction?.count === 'vertical' &&
    repeatingFormulaResolved > 0
      ? [
          {
            name: 'height',
            formula: [
              'repeating_item_spacing',
              '*',
              '(',
              'repeating_item_count',
              '-',
              '1',
              ')',
              '+',
              'repeating_item_thickness',
            ],
          } as IAutoDependency,
        ]
      : []),
  ];
};

const densityAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  ...getVolumeDependencies(
    replacements,
    'density',
    [...replacements.mass, '/', 'fill', '/'],
    false,
  ),
];

const densityArealSideAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  {
    name: 'density_areal_side',
    formula: [...replacements.mass, '/', 'area_side'],
  },
  {
    name: 'density_areal_side',
    formula: [...replacements.mass, '/', '(', 'length', '*', 'height', ')'],
  },
];

const densityArealTopAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  {
    name: 'density_areal_top',
    formula: [...replacements.mass, '/', 'area_top'],
  },
  {
    name: 'density_areal_top',
    formula: [...replacements.mass, '/', '(', 'length', '*', 'width', ')'],
  },
];

const fillAutoDependencies = (
  replacements: Replacements,
): IAutoDependency[] => [
  ...getVolumeDependencies(
    replacements,
    'fill',
    ['(', ...replacements.mass, '/', 'density', ')', '/'],
    false,
  ),
];

// NOSONAR
// const repeatingItemSpacingAutoDependencies = (
//   quantity: ElementQuantityRecord,
// ): IAutoDependency[] =>
//   !quantity.repeating_items?.count
//     ? []
//     : [
//         {
//           name: 'repeating_item_spacing',
//           formula: [
//             'firstNonZero(',
//             '(',
//             quantity.repeating_direction?.count === 'horizontal'
//               ? 'width'
//               : 'height',
//             '-',
//             'repeating_item_thickness',
//             ')',
//             '/',
//             '(',
//             'repeating_item_count',
//             '-',
//             '1',
//             ')',
//             ',',
//             'repeating_item_thickness',
//             ')',
//           ],
//         },
//       ];

// NOSONAR
// const repeatingItemThicknessAutoDependencies = (
//   quantity: ElementQuantityRecord,
// ): IAutoDependency[] =>
//   !quantity.repeating_items?.count
//     ? []
//     : [
//         {
//           name: 'repeating_item_thickness',
//           formula: [
//             'firstNonZero(',
//             quantity.repeating_direction?.count === 'horizontal'
//               ? 'width'
//               : 'height',
//             '-',
//             'repeating_item_spacing',
//             '*',
//             '(',
//             'repeating_item_count',
//             '-',
//             '1',
//             ')',
//             ',',
//             'repeating_item_spacing',
//             ')',
//           ],
//         },
//       ];

// NOSONAR
// const repeatingItemCountAutoDependencies = (
//   quantity: ElementQuantityRecord,
// ): IAutoDependency[] =>
//   !quantity.repeating_items?.count
//     ? []
//     : [
//         {
//           name: 'repeating_item_count',
//           formula: [
//             '(',
//             quantity.repeating_direction?.count === 'horizontal'
//               ? 'width'
//               : 'height',
//             '-',
//             'repeating_item_thickness',
//             ')',
//             '/',
//             'repeating_item_spacing',
//             '+',
//             '1',
//           ],
//         },
//       ];

const inheritableAutoDependencies: IAutoDependency[] =
  INHERITABLE_QUANTITY_NAMES.map((name) => ({
    name,
    formula: [name],
  }));

const getMassRepeatingItemsReplacementCondition = (
  quantity: ElementQuantityRecord,
) =>
  quantity.repeating_items?.count &&
  hasNonZeroRepeatingQuantity(quantity.repeating_item_thickness) &&
  hasNonZeroRepeatingQuantity(quantity.repeating_item_spacing);

const getReplacements = (quantity: ElementQuantityRecord): Replacements => ({
  length: [],
  width: [],
  height: [],
  area_side: [],
  area_top: [],
  area_section: [],
  volume: [],
  mass: getMassRepeatingItemsReplacementCondition(quantity)
    ? [
        '(',
        'mass',
        '/',
        '(',
        'repeating_item_thickness',
        '/',
        'repeating_item_spacing',
        ')',
        ')',
      ]
    : ['mass'],
  density: [],
  density_areal_side: [],
  density_areal_top: [],
  fill: [],
  repeating_item_spacing: [],
  repeating_item_thickness: [],
  repeating_item_count: [],
  time: [],
  energy: [],
  pieces: [],
});

export const autoDependencies = (
  quantity: ElementQuantityRecord,
): Readonly<IAutoDependency>[] => {
  const replacements = getReplacements(quantity);

  return [
    ...volumeAutoDependencies(replacements),
    ...lengthAutoDependencies(replacements),
    ...widthAutoDependencies(quantity, replacements),
    ...heightAutoDependencies(quantity, replacements),
    ...areaSideAutoDependencies(replacements),
    ...areaTopAutoDependencies(replacements),
    ...areaSectionAutoDependencies(replacements),
    ...massAutoDependencies(quantity, replacements),
    ...densityAutoDependencies(replacements),
    ...densityArealSideAutoDependencies(replacements),
    ...densityArealTopAutoDependencies(replacements),
    ...fillAutoDependencies(replacements),
    // ...repeatingItemSpacingAutoDependencies(quantity),
    // ...repeatingItemThicknessAutoDependencies(quantity),
    // ...repeatingItemCountAutoDependencies(quantity),
    ...inheritableAutoDependencies,
  ];
};

/**
 * Get all dependencies for a formula. I.E. filter out operators
 * @param formula
 * @returns
 */
const getDependencies = (
  formula: IAutoDependency['formula'],
): (keyof ElementQuantityExpressionRecord)[] =>
  formula.filter((part) => isElementQuantityName(part));

/**
 * Util to sort formulas by priority (highest prio first).
 * Currently it should prioritize length width and height.
 * This should adress the inheritance issue below:
 * @example
 * parent.length = 3
 * parent.width = 4
 * parent.area_top = 12
 *
 * width = 8
 * height = 10
 * volume = parent.area_top * height = 12 * 10 = 120 // Wrong
 * volume = parent.length * width * height = 3 * 8 * 10 = 240 // Correct
 * @param formula
 * @returns
 */
const getFormulaPrio = (formula: Formula): number => {
  const lowestPrio: FormulaPart[] = ['area_side', 'area_top', 'area_section'];
  const firstPart = formula[0];

  // Direct inheritable properties (length inheriting parent.length) have prio 1 unless they are lowest prio properties
  if (
    formula.length === 1 &&
    INHERITABLE_QUANTITY_NAMES.includes(
      formula[0] as ElementQuantityExpressionName,
    ) &&
    firstPart &&
    !lowestPrio.includes(firstPart) &&
    ALLOW_FALLBACK_REFERENCES.includes(formula[0] as ElementQuantityName)
  ) {
    return 1;
  }

  return formula.reduce((acc, part) => {
    if (ALLOW_FALLBACK_REFERENCES.includes(part as ElementQuantityName)) {
      return acc - 3;
    }

    if (lowestPrio.includes(part)) {
      return acc - 1;
    }
    return acc;
  }, 0);
};

/**
 * Get all formulas that are relevant for a certain quantity property.
 * Also make sure the formula won't reference itself unless it's an inheritable quantity.
 * @param name
 * @param inheritable
 * @param deps Pre-computed result of autoDependencies(quantity); pass when calling for many names with same quantity
 * @returns
 */
const getRelevantAutoFormulas = (
  name: keyof ElementQuantityRecord,
  inheritable: (keyof ElementQuantityRecord)[] = [],
  deps: Readonly<IAutoDependency>[],
): IAutoDependency['formula'][] => {
  return deps
    .filter(
      (dep) =>
        dep.name === name &&
        (!dep.formula.includes(name) || inheritable.includes(name)),
    )
    .sort((a, b) => getFormulaPrio(b.formula) - getFormulaPrio(a.formula))
    .map((dep) => dep.formula);
};

export const getParentPropertyExpression = (
  name: keyof ElementQuantityRecord,
): string => `${GET_PARENT_VALUE_FN_NAME}("${name}")`;

export const autoFormulaToExpression = (formula: string[] = []): string => {
  return (
    formula
      .join(' ')
      .replaceAll('( ', '(')
      .replaceAll(' )', ')')
      .replaceAll(' ,', ',') || DEFAULT_QUANTITY_EXPRESSION
  );
};

const isClearAllowedOnCombination = (
  names: ElementQuantityName[],
  record: ElementQuantityRecord,
  variables: ExpressionVariables,
): boolean => {
  // All related properties must exist for something to be obsolete
  return names.every((name) => {
    const property = record[name];
    if (hasCount(property)) {
      return hasExplicitQuantityCount(property);
    }
    // If fill > 100% we should count that as a set value (for something to be cleared)
    else if (name === 'fill' && hasFallbackCount(property)) {
      const count = property.fallbackCount;

      return (
        count.expression !== DEFAULT_QUANTITY_EXPRESSIONS.fill &&
        getNextResolvedValue(property, variables)
      );
    }
    return false;
  });
};

const findObsoleteQuantityCombination = (
  quantity: ElementQuantityRecord,
  variables: ExpressionVariables,
): ElementQuantityName[] | undefined => {
  const deps = autoDependencies(quantity);
  return getKeys(quantity)
    .map(
      (name) =>
        getRelevantAutoFormulas(name, [], deps)
          .map((formula) => {
            const names = [name, ...getDependencies(formula)];

            if (isClearAllowedOnCombination(names, quantity, variables)) {
              return names.filter((n) => hasExplicitQuantityCount(quantity[n]));
            }
          })
          .filter(isDefined)[0],
    )
    .filter(isDefined)[0];
};

const findForbiddenQuantityCombination = (
  quantity: ElementQuantityRecord,
): ElementQuantityName[] => {
  const spacing = quantity.repeating_item_spacing;
  const thickness = quantity.repeating_item_thickness;
  const spacingResolved = getRepeatingQuantityResolved(spacing);
  const thicknessResolved = getRepeatingQuantityResolved(thickness);
  if (
    hasCount(spacing) &&
    hasCount(thickness) &&
    spacingResolved !== undefined &&
    thicknessResolved !== undefined &&
    spacingResolved < thicknessResolved
  ) {
    return ['repeating_item_spacing', 'repeating_item_thickness'];
  }
  return [];
};

/**
 * True when `expression` uses `qtyName` as the dividend in an auto-inverse formula
 * (e.g. `((mass / density) / fill) / width`), not as a forward factor (`height * mass_per_m`).
 */
const referencesQuantityAsInverseSource = (
  expression: string,
  qtyName: ElementQuantityName,
): boolean => {
  if (!getVariablesInExpression(expression).includes(qtyName)) return false;
  const inversePattern = new RegExp(`(?:\\(|\\s|^)${qtyName}\\s*/`);
  return inversePattern.test(expression);
};

/**
 * Clears quantity `count` rows whose expression still references another quantity
 * that no longer has a user `count` (e.g. area derived from mass after mass was cleared).
 * Those inverse formulas block {@link getAutoFallbackCount} via circular dependency checks.
 *
 * @param quantity
 * @returns
 */
export const clearStaleDerivedQuantityCounts = (
  quantity: ElementQuantityRecord,
): ElementQuantityRecord => {
  let updated = quantity;

  for (const prop of Object.values(updated)) {
    if (!isElementQuantityExpressionProperty(prop) || !hasCount(prop)) {
      continue;
    }

    const referencedWithoutCount = getVariablesInExpression(
      prop.count.expression,
    ).filter(
      (name): name is ElementQuantityName =>
        isElementQuantityName(name) &&
        !hasCount(updated[name]) &&
        referencesQuantityAsInverseSource(prop.count.expression, name),
    );

    if (!referencedWithoutCount.length) continue;

    const count = prop.count;
    // Keep explicit numeric overrides (value) when dropping a stale inverse formula.
    const preservedExpression =
      isExpressionValue(count) && count.value !== undefined
        ? String(count.value)
        : undefined;

    updated = applyChanges(updated, {
      [prop.name]: preservedExpression
        ? { count: createExpression(preservedExpression) }
        : { count: undefined },
    });
  }

  return updated;
};

/**
 * Will clear obsolete quantities.
 * Sample: if user inputs length, width and height,
 * then volume is obsolete and can use auto/fallback value instead
 * @param quantity
 * @returns
 */
export const autoClearObsoleteQuantities = (
  quantity: ElementQuantityRecord,
  variables: ExpressionVariables = defaultVariables,
): ElementQuantityRecord => {
  const obsoleteCombo = findObsoleteQuantityCombination(quantity, variables);
  const forbiddenCombo = findForbiddenQuantityCombination(quantity);

  if (!obsoleteCombo?.length && !forbiddenCombo?.length) {
    return quantity;
  }

  const properties = sortQuantityPropertiesByUpdatedAt(quantity);
  // Combos only ever name expression quantities; narrow so the fallback reset
  // below cannot land on a select/switch row.
  const oldestProperty = properties
    .filter(isElementQuantityExpressionProperty)
    .find(
      ({ name }) =>
        forbiddenCombo.includes(name) || obsoleteCombo?.includes(name),
    );

  if (!oldestProperty) {
    throw new Error('Could not find oldest property');
  }

  // Remove count from oldest property
  const updatedQuantity = applyChanges(quantity, {
    [oldestProperty.name]: {
      count: undefined,
      fallbackCount: createExpression(
        getDefaultFallbackExpression(oldestProperty),
      ),
    },
  });

  // Make it recursive to clear all obsolete properties
  return autoClearObsoleteQuantities(updatedQuantity, variables);
};

/**
 * If a quantity record has circular dependencies we need to reset any fallback counts
 * @param quantity
 * @returns
 */
export const clearCircularFallbackCounts = (
  quantity: ElementQuantityRecord,
): ElementQuantityRecord => {
  if (hasCircularDependencies(Object.values(quantity))) {
    return Object.values(quantity).reduce(
      (acc, prop) => ({
        ...acc,
        // Select/switch rows never take an expression fallback (DEV-3704).
        [prop.name]: isElementQuantityExpressionName(prop.name)
          ? {
              ...prop,
              fallbackCount: createExpression(
                getDefaultFallbackExpression(
                  prop as IElementQuantityExpressionProperty,
                ),
              ),
            }
          : prop,
      }),
      {} as ElementQuantityRecord,
    );
  }
  return quantity;
};

const sortQuantityPropertiesByUpdatedAt = (
  quantities: ElementQuantityRecord,
): IElementQuantityProperty[] => {
  const oldestDate = '2022-05-22T10:33:14.183Z';
  return Object.values(quantities).sort((a, b) => {
    const dateA = a.updated_at ?? oldestDate;
    const dateB = b.updated_at ?? oldestDate;

    if (dateA === dateB) {
      return 0;
    }

    return dateA < dateB ? -1 : 1;
  });
};

/**
 * Get a valid formula part to use in an expression. (by looping all FormulaParts)
 * @param propertyName Name of the property we currently are trying to fallback
 * @param part Part of a formula (like length, width, height, +, -, *, /)
 * @param quantityRecord
 * @param inheritableQuantityNames Which properties that can be inherited from parents
 * @returns string formula part or undefined if it is not valid
 */
const getFormulaPart = (
  propertyName: keyof ElementQuantityExpressionRecord,
  part: FormulaPart,
  quantityRecord: ElementQuantityRecord,
  inheritableQuantityNames: ElementQuantityName[],
  variables: ExpressionVariables,
): string | undefined => {
  // Checks if it is an operator (like '*') or quantity prop, return if an operator
  if (!isElementQuantityName(part)) {
    return part;
  }

  const partQuantity = quantityRecord[part];

  // Count refer to that property directly. Also make sure to avoid 0 divisions
  if (
    hasCount(partQuantity) &&
    (partQuantity.count.expression !== DEFAULT_QUANTITY_EXPRESSION ||
      guards.is.number(partQuantity.count.value))
  ) {
    return part;
  }

  // Use parent value if possible (return something like `getParentValue("length")` )
  if (inheritableQuantityNames.includes(part)) {
    // When areal density is available, mass must not use inherited area rows that resolve to 0 (DEV-3459)
    if (
      propertyName === 'mass' &&
      areaQuantityNames.includes(part as (typeof areaQuantityNames)[number]) &&
      isElementQuantityExpressionProperty(partQuantity) &&
      hasResolvedArealDensity(quantityRecord, variables) &&
      !hasResolvedNonZeroQuantity(partQuantity, variables)
    ) {
      return undefined;
    }
    return getParentPropertyExpression(part);
  }

  // For fill and density we can use the fallback count if it is not the default
  if (
    isAllowedFallbackReference(propertyName, quantityRecord, part, variables)
  ) {
    return part;
  }

  // If it doesn't return anything this formula is not valid and will be ignored
};

/**
 * Check if the forumla or string contains a reference to a parent property
 * @param parts
 * @returns
 */
const hasParentReference = (...parts: string[]): boolean => {
  return parts.some((part) => getParentReferenceCount(part) > 0);
};

/**
 * Get how many parent references that are used in a formula
 * @param parts
 * @returns
 */
const getParentReferenceCount = (expression: string): number =>
  countOccurrences(expression, GET_PARENT_VALUE_FN_NAME);

export const getNonParentReferenceCount = (expression: string): number =>
  getVariablesInExpression(expression).filter(isElementQuantityName).length;

/**
 * Get a value between 0 and 1 that represents how much a formula is using parent reference.
 * (0 = no parent reference, 1 = only parent references)
 * @param expression
 * @returns
 */
export const getParentReferenceRatio = (expression: string): number => {
  const parentReferenceCount = getParentReferenceCount(expression);
  const nonParentReferenceCount = getNonParentReferenceCount(expression);

  if (parentReferenceCount === 0) {
    return 0;
  }

  return (
    parentReferenceCount / (parentReferenceCount + nonParentReferenceCount)
  );
};

/**
 * Get a calculated fallbackCount based on inheritance
 * and available count values (user input)
 * @param prop
 * @param quantity
 * @param inheritable
 * @returns
 */
const getAutoFallbackCount = (
  prop: IElementQuantityExpressionProperty,
  quantity: ElementQuantityRecord,
  inheritable: ElementQuantityName[],
  variables: ExpressionVariables,
): ExpressionValue | undefined => {
  if (!isAllowedToUseInheritedQuantities(prop.name)) {
    inheritable = [];
  }

  // Keep resolved values
  const createFallbackCountIfModified = (
    expression: string,
  ): ExpressionValue => {
    const fallbackCount = prop.fallbackCount;
    return fallbackCount?.expression === expression
      ? fallbackCount
      : createExpression(expression);
  };

  // Non quantity properties can not have auto fallback count
  if (!isElementQuantityExpressionProperty(prop)) {
    return undefined;
  }

  // Count is set, do not use fallbackCount
  if (hasCount(prop)) {
    return createFallbackCountIfModified(getDefaultFallbackExpression(prop));
  }

  // All formulas that are relevant for this quantity property
  const relevantFormulas = getRelevantAutoFormulas(
    prop.name,
    inheritable,
    autoDependencies(quantity),
  );

  // Property can be directly inherited from parents
  const matchedExpressions: string[] = [];

  for (const formula of relevantFormulas) {
    const formulaParts = formula.map((formulaPart) =>
      getFormulaPart(prop.name, formulaPart, quantity, inheritable, variables),
    );

    // All variables in formula can be referenced
    if (formulaParts.every(isDefined)) {
      const expression = autoFormulaToExpression(formulaParts);

      // Save computing by return the first "perfect match"
      if (
        !hasParentReference(expression) &&
        isValidFallbackCountExpression(quantity, prop.name, expression)
      ) {
        return createFallbackCountIfModified(expression);
      }
      matchedExpressions.push(expression);
    }
  }

  // Prefer direct parent inheritance over derived getParentValue formulas (DEV-3365)
  if (inheritable.includes(prop.name)) {
    const directInherit = getParentPropertyExpression(prop.name);
    if (isValidFallbackCountExpression(quantity, prop.name, directInherit)) {
      return createFallbackCountIfModified(directInherit);
    }
  }

  // Only matches with parent references so we need to use them anyway
  if (matchedExpressions.length) {
    // Get first expression that does not cause circular dependencies
    const match = matchedExpressions
      .sort((a, b) => getParentReferenceRatio(a) - getParentReferenceRatio(b))
      .find((expression) =>
        isValidFallbackCountExpression(quantity, prop.name, expression),
      );

    return createFallbackCountIfModified(
      match ?? getDefaultFallbackExpression(prop),
    );
  }

  // Property can be directly inherited from parents (width = parent.width)
  if (inheritable.includes(prop.name)) {
    return createFallbackCountIfModified(
      getParentPropertyExpression(prop.name),
    );
  }

  // Parent no longer exposes this quantity — keep recipe/EL inherit line (DEV-3354)
  if (
    !hasCount(prop) &&
    prop.fallbackCount &&
    !inheritable.includes(prop.name) &&
    (isInheritanceSupported(prop) ||
      prop.fallbackCount.expression?.includes(GET_PARENT_VALUE_FN_NAME))
  ) {
    return prop.fallbackCount;
  }

  return createFallbackCountIfModified(getDefaultFallbackExpression(prop));
};

/**
 * Apply auto fallback counts to all quantity properties and return a new quantity record.
 * Will also clear redundant quantity properties.
 * @param quantity
 * @param inheritableQuantityNames List of all inheritable quantity names
 * @returns
 */
export const autoUpdateQuantityRecord = (
  clearedQuantity: ElementQuantityRecord,
  inheritableQuantityNames: ElementQuantityName[] = [],
  variables: ExpressionVariables = defaultVariables,
): ElementQuantityRecord => {
  // Select/switch rows (repeating_items, repeating_direction) stay in the
  // working record — the replacements read them — but never get a fallback.
  const sortedProperties = Object.values(clearedQuantity)
    .filter(isElementQuantityExpressionProperty)
    .filter((prop) => !AUTO_QUANTITY_EXCLUDED_NAMES.has(prop.name))
    .sort((a, b) => getAutoResolvePriority(b) - getAutoResolvePriority(a));

  // Single mutable merge: same semantics as the old reduce spread but O(n) object growth.
  const working: ElementQuantityRecord = { ...clearedQuantity };
  const updates: ElementQuantityExpressionRecord =
    {} as ElementQuantityExpressionRecord;

  for (const prop of sortedProperties) {
    const fallbackCount = getAutoFallbackCount(
      prop,
      working,
      inheritableQuantityNames,
      variables,
    );
    const resolvedFallback = fallbackCount ?? prop.fallbackCount;
    if (resolvedFallback === undefined) {
      updates[prop.name] = prop;
      working[prop.name] = prop;
      continue;
    }
    const updated = { ...prop, fallbackCount: resolvedFallback };
    updates[prop.name] = updated;
    working[prop.name] = updated;
  }

  return applyChanges(clearedQuantity, updates);
};

/**
 * Check if a property can use inherited values from parent
 * @param name
 * @returns
 */
const isAllowedToUseInheritedQuantities = (
  name: ElementQuantityExpressionName,
): boolean => !FORBIDDEN_INHERITANCE.includes(name);

/**
 * True when the parent has at least one area quantity row that is user-set or formula-driven
 * (not a bare template placeholder). Used so children can inherit sibling area rows via
 * getParentValue when e.g. wall has area_side but area_top is still default (DEV-3365).
 */
const parentExposesMeaningfulAreaQuantities = (
  quantity: ElementQuantityExpressionRecord,
): boolean => {
  for (const areaName of areaQuantityNames) {
    const row = quantity[areaName];
    if (!row) continue;
    if (hasCount(row)) return true;
    if (
      row.fallbackCount?.expression !== DEFAULT_QUANTITY_EXPRESSION ||
      !isDefaultFallbackCount(row)
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Get quantity names the child may inherit via {@link getParentValue}.
 * {@link path} is root → nearest parent (same as {@link enrichElementStructure}).
 * Qualifying names are claimed nearest-first so a closer parent with a real
 * value wins over an outer placeholder folder (DEV-3582), while a nearer
 * placeholder does not block a farther parent that actually exposes the quantity.
 */
export const getInheritableQuantities = (
  element: IElement,
  path: OneOfParentElements[] = [],
): (keyof ElementQuantityExpressionRecord)[] => {
  // Set avoids O(depth × names) `includes` scans when the path is deep.
  const seen = new Set<keyof ElementQuantityExpressionRecord>();
  const inheritableFromParents: (keyof ElementQuantityExpressionRecord)[] = [];

  // Nearest parent first — matches getParentValue walking `scope.parent`.
  for (let i = path.length - 1; i >= 0; i--) {
    const parent = path[i];
    if (!parent) continue;
    const quantity = getElementQuantityExpressionRecord(parent);
    const parentHasMeaningfulAreas =
      parentExposesMeaningfulAreaQuantities(quantity);
    for (const rawName of getKeys(quantity)) {
      if (!isElementQuantityName(rawName)) continue;
      const name = rawName;
      if (seen.has(name)) continue;
      const row = quantity[name];
      if (!row || !INHERITABLE_QUANTITY_NAME_SET.has(name)) continue;

      const isAreaName = areaQuantityNames.includes(
        name as (typeof areaQuantityNames)[number],
      );
      const isPlaceholderDefault =
        row.fallbackCount?.expression === DEFAULT_QUANTITY_EXPRESSION &&
        isDefaultFallbackCount(row);

      // When any parent area is set (e.g. wall area_side), inherit all parent area rows
      // including template-default area_top so children get getParentValue (DEV-3365).
      const qualifies = isAreaName
        ? parentHasMeaningfulAreas
        : hasCount(row) || !isPlaceholderDefault;
      // Only claim names that qualify — placeholders must not shadow a farther parent.
      if (!qualifies) continue;
      seen.add(name);
      inheritableFromParents.push(name);
    }
  }

  return removeForbiddenInheritableQuantities(element, inheritableFromParents);
};
/**
 * Remove quantitities
 * @param element
 * @param inheritable
 * @returns
 */
const removeForbiddenInheritableQuantities = (
  element: IElement,
  inheritable: (keyof ElementQuantityExpressionRecord)[],
): (keyof ElementQuantityExpressionRecord)[] => {
  const quantity = getElementQuantityExpressionRecord(element);

  // Make copy with the inheritable quantities that are not set by the user
  const filteredInheritable = inheritable.filter(
    (name) => !hasCount(quantity[name]),
  );

  // If all dimensions can still be inherited from parents and volume or mass
  // (mass can be used to calculate volume) is set we need clear inheritable to not create paradoxes
  if (
    (hasCount(quantity['volume']) || hasCount(quantity['mass'])) &&
    includesAll(filteredInheritable, ...dimensionQuantityNames)
  ) {
    const first = filteredInheritable.find((name) =>
      dimensionQuantityNames.includes(name as DimensionQuantityName),
    );

    return first
      ? cloneRemoveItems(filteredInheritable, first, ...areaQuantityNames)
      : cloneRemoveItems(filteredInheritable, ...areaQuantityNames);
  }

  return filteredInheritable;
};

/**
 * Update quantity properties for an element including all of its children.
 * This will clear redundant quantity properties and add auto fallbackCounts where applicable.
 * @param element
 * @param path Search path to element, used to calculate inheritance
 * @param variablesRecord Expression variables per element id, used to resolve quantity counts
 * @param options `cacheNamespace` partitions the {@link AutoUpdateSignature} dirty-check so
 *   compute / proposal-enrich / persist trees don't share signatures (see {@link AutoUpdateCacheNamespace}).
 *   `forceRecompute` runs auto-quantity even when the namespaced signature is unchanged — used on
 *   freshly resolved proposal trees where the input element does not yet carry the last result.
 * @returns
 */
export const autoUpdateElementQuantity = (
  element: IElement,
  path: OneOfParentElements[] = [],
  variablesRecord: ExpressionVariablesRecord = {},
  options: {
    cacheNamespace: AutoUpdateCacheNamespace;
    forceRecompute?: boolean;
  } = { cacheNamespace: 'persist' },
): IElement => {
  // Full record: the auto formulas read the repeating_items / repeating_direction rows,
  // and the self-heal below must run even when only those rows exist.
  if (!Object.keys(getElementQuantityRecord(element)).length) {
    return element;
  }
  // Heal select/switch rows persisted with an expression fallback (DEV-3704)
  // on the next pipeline pass, so the stored row stops failing validation.
  const fallbackClears = getInvalidSelectSwitchFallbackClears(element);
  if (fallbackClears) {
    element = applyChanges(element, { quantity: fallbackClears });
  }
  const quantity = getElementQuantityRecord(element);
  const inheritableQuantityNames = getInheritableQuantities(element, path);

  // Remove any obsolete property.counts (like volume if length, width and height is set most recently).
  // Also clear inverse formulas that still reference a cleared quantity (e.g. mass).
  // Also clear all fallbacks on circular error
  const clearedQuantity = clearCircularFallbackCounts(
    autoClearObsoleteQuantities(
      clearStaleDerivedQuantityCounts(quantity),
      variablesRecord[element.id],
    ),
  );

  const elementVariables = variablesRecord[element.id];
  const cacheKey = `${options.cacheNamespace}:${element.id}`;
  const signature = elementVariables
    ? buildAutoUpdateSignature(
        { ...element, quantity: clearedQuantity },
        inheritableQuantityNames,
        elementVariables,
      )
    : undefined;

  if (signature && !options.forceRecompute) {
    const cached = shouldAutoUpdateCacheRecord[cacheKey];
    if (cached && isEqualAutoUpdateSignature(cached, signature)) return element;
  }

  const updated = applyChanges(element, {
    quantity: autoUpdateQuantityRecord(
      clearedQuantity,
      inheritableQuantityNames,
      variablesRecord[element.id],
    ),
  });

  if (signature) {
    rememberAutoUpdateSignature(cacheKey, signature, updated === element);
  }

  return updated;
};

/**
 * Cache namespace for the {@link AutoUpdateSignature} dirty-check.
 *
 * The dirty-check skips re-running auto-quantity when an element's inputs are unchanged since the
 * last visit. Different consumers build different trees for the same `element.id`, so they must
 * not share signatures (DEV-3349):
 * - `persist`: interactive/persisted enrich — elements carry their previous auto-update result.
 * - `proposal-enrich`: freshly resolved proposal trees ({@link enrichResolvedTree}).
 * - `compute`: freshly built compute trees (compute pipeline).
 */
export type AutoUpdateCacheNamespace =
  'persist' | 'proposal-enrich' | 'compute';

/** Inputs {@link autoUpdateQuantityRecord} reads, reduced to comparable strings. */
type AutoUpdateSignature = {
  inheritable: ElementQuantityName[];
  definedProperties: string[];
};

const shouldAutoUpdateCacheRecord: Record<string, AutoUpdateSignature> = {};

/** Clears {@link shouldAutoUpdateCacheRecord} between unit tests (module-level memo). */
export const resetAutoUpdateQuantityCacheForTests = (): void => {
  for (const key of Object.keys(shouldAutoUpdateCacheRecord)) {
    delete shouldAutoUpdateCacheRecord[key];
  }
};

/**
 * Reduce everything {@link autoUpdateQuantityRecord} reads to a comparable {@link AutoUpdateSignature}:
 * quantity expressions, resolved property counts, and whether each density resolves to non-zero.
 */
const buildAutoUpdateSignature = (
  element: IElement,
  inheritable: ElementQuantityName[],
  variables: ExpressionVariables,
): AutoUpdateSignature => {
  const record = getElementQuantityRecord(element);

  const definedProperties = [
    ...getQuantityAutoUpdateCacheSignatures(record),
    ...getElementProperties(element).map((prop) => {
      return prop.name + ':' + getElementPropertyResolvedCount(prop);
    }),
  ];

  updateDensity(record, variables, definedProperties, 'density');
  updateDensity(record, variables, definedProperties, 'density_areal_side');
  updateDensity(record, variables, definedProperties, 'density_areal_top');

  return { inheritable, definedProperties };
};

const isEqualAutoUpdateSignature = (
  a: AutoUpdateSignature,
  b: AutoUpdateSignature,
): boolean =>
  shallowEqual(a.inheritable, b.inheritable) &&
  shallowEqual(a.definedProperties, b.definedProperties);

/**
 * Remember a signature only when auto-update was a no-op, so a cached skip always means
 * "the result is already on the element".
 *
 * Skipping on matching inputs assumes the element carries the previous result. That holds for a
 * tree that is re-visited after being updated, but not for a tree built from scratch: compute and
 * proposal resolve rebuild trees on every pass and reuse element ids (recipe children get the
 * deterministic `:r:` address), so the same signature can belong to an element that has never had
 * the result applied. Caching only no-ops removes the ambiguity — a signature that produced a
 * change is dropped, and the next visit re-runs and lands on the idempotent no-op that is then
 * safe to cache (DEV-3656).
 */
const rememberAutoUpdateSignature = (
  cacheKey: string,
  signature: AutoUpdateSignature,
  wasNoOp: boolean,
): void => {
  if (wasNoOp) {
    shouldAutoUpdateCacheRecord[cacheKey] = signature;
    return;
  }
  delete shouldAutoUpdateCacheRecord[cacheKey];
};

const updateDensity = (
  record: ElementQuantityRecord,
  variables: ExpressionVariables,
  definedProperties: string[],
  propertyName: 'density' | 'density_areal_side' | 'density_areal_top',
) => {
  const density = record[propertyName];
  const densityValue =
    density && !hasCount(density)
      ? getNextResolvedValue(density, variables)
      : 0;

  // If products are changed we need to update and in this case densityValue would go from 0 to a larger value
  if (densityValue !== 0) {
    definedProperties.unshift(propertyName);
  }
};

/**
 * If auto fallback is defined for a quantity property
 * @param quantity
 * @returns
 */
const isDefaultFallbackCount = (
  quantity?: IElementQuantityExpressionProperty,
): ReturnType<typeof hasFallbackCount> =>
  hasFallbackCount(quantity) &&
  quantity.fallbackCount.expression === getDefaultFallbackExpression(quantity);

/**
 * Get the default fallback expression for a certain quantity property.
 * Note that different categories might have different default fallback expressions
 * for the same property (like insulation => density)
 * @param prop
 * @returns
 */
export const getDefaultFallbackExpression = (
  prop: IElementQuantityExpressionProperty,
): string => {
  const category_id = String(prop.category_id);
  const defaultByName = getDefaultFallbackExpressionByName(prop.name);

  if (!category_id) {
    return prop.fallbackCount?.expression ?? defaultByName;
  }

  // Check if the quantity record for this category already exists
  let quantity = defaultQuantityRecordByCategoryId.get(category_id);

  // Create the quantity record if it doesn't exist (super expensive to do a lot of times)
  if (quantity === undefined) {
    const category = getElementCategory(prop);
    quantity = createElementQuantityRecord(category?.getQuantityProperties?.());
    defaultQuantityRecordByCategoryId.set(category_id, quantity);
  }

  // Some categories might have a custom fallback expression for certain quantity properties
  return quantity?.[prop.name]?.fallbackCount?.expression ?? defaultByName;
};

/**
 * Get the NEXT resolved value for a quantity property.
 * When calculating auto fallback counts we have the previous resolved values
 * since they have not yet been updated
 * @param prop
 * @param variables
 * @returns
 */
const getNextResolvedValue = (
  prop: IElementQuantityExpressionProperty,
  variables: ExpressionVariables,
): number => {
  const count = getCount(prop);
  const expression = count.expression;

  if (isNumeric(expression)) {
    return Number(expression);
  }

  // Cache this since it's heavy and might run many times
  return cacheFactory(
    () => errorHandledSolve(count, variables).resolved,
    `getNextResolvedValue[${String(prop.id ?? '')}]`,
    [expression, variables],
  );
};

/**
 * True when a quantity expression resolves to a non-zero value.
 * Prefer stale fallbackCount.resolved, then evaluate the next value (DEV-3349 / DEV-3354).
 */
const hasResolvedNonZeroQuantity = (
  part: IElementQuantityExpressionProperty,
  variables: ExpressionVariables,
): boolean => {
  if (!(hasCount(part) || hasFallbackCount(part))) return false;
  const staleResolved = part.fallbackCount?.resolved;
  if (guards.is.finiteNumber(staleResolved) && staleResolved > 0) {
    return true;
  }
  return getNextResolvedValue(part, variables) !== 0;
};

/**
 * True when a volumetric `density` resolves to a non-zero value.
 * Volumetric density takes precedence over areal density (kg/m²) as the mass path (DEV-3349).
 */
const hasResolvedVolumetricDensity = (
  quantity: ElementQuantityRecord,
  variables: ExpressionVariables,
): boolean => {
  const densityPart = quantity.density;
  if (!isElementQuantityExpressionProperty(densityPart)) return false;
  return hasResolvedNonZeroQuantity(densityPart, variables);
};

/**
 * True when an areal density property resolves to a non-zero value.
 */
const hasResolvedArealDensity = (
  quantity: ElementQuantityRecord,
  variables: ExpressionVariables,
): boolean =>
  AREAL_DENSITY_PARTS.some((name) => {
    const part = quantity[name];
    if (!isElementQuantityExpressionProperty(part)) return false;
    return hasResolvedNonZeroQuantity(part, variables);
  });

/**
 * Check if a fallback reference is allowed for a certain quantity property
 * @param propertyName
 * @param quantity
 * @param part
 * @returns
 */
const isAllowedFallbackReference = (
  propertyName: ElementQuantityName,
  quantity: ElementQuantityRecord,
  part: ElementQuantityName,
  variables: ExpressionVariables,
): boolean => {
  const quantityPart = quantity[part];
  const fallbackCount = quantityPart?.fallbackCount;
  const expression = (fallbackCount as ExpressionValue)?.expression;

  // getParentValue inherit rows — mass may reference parent area before parent row updates (DEV-3354)
  if (
    propertyName === 'mass' &&
    (part === 'area_side' || part === 'area_top') &&
    hasFallbackCount(quantityPart) &&
    expression?.includes(GET_PARENT_VALUE_FN_NAME) &&
    isElementQuantityExpressionProperty(quantityPart)
  ) {
    return hasResolvedNonZeroQuantity(quantityPart, variables);
  }

  if (
    ALLOW_FALLBACK_REFERENCES.includes(part) &&
    hasFallbackCount(quantityPart) &&
    expression &&
    expression !== DEFAULT_QUANTITY_EXPRESSION
  ) {
    const expressionVars = getVariablesInExpression(expression);

    // Allow fallback reference if it is not referencing itself
    if (expressionVars.includes(propertyName)) {
      return false;
    }

    // Mass references product-sourced inputs (density / fill / areal density) that may resolve to 0
    // during auto-update before product vars load. When volumetric density is missing but areal
    // density is available, prefer the areal path (DEV-3349). When neither is resolved yet, keep
    // allowing volumetric density so deferred product vars can still populate the formula.
    if (propertyName === 'mass' && ALLOW_FALLBACK_REFERENCES.includes(part)) {
      if (part === 'fill') return true;

      const hasVolumetricDensity = hasResolvedVolumetricDensity(
        quantity,
        variables,
      );
      const hasArealDensity = hasResolvedArealDensity(quantity, variables);

      if (part === 'density') {
        if (!hasVolumetricDensity && hasArealDensity) return false;
        if (!hasVolumetricDensity && !hasArealDensity) return true;
        return hasVolumetricDensity;
      }

      if (AREAL_DENSITY_PARTS.includes(part)) {
        return !hasVolumetricDensity;
      }
    }

    const value = getNextResolvedValue(quantityPart, variables);
    if (value !== 0) return true;

    return false;
  }
  return false;
};

/**
 * Test if fallback expression is valid for a certain quantity property. Currently only test for circular dependencies
 * @param record
 * @param name
 * @param expression
 * @returns
 */
const isValidFallbackCountExpression = (
  record: ElementQuantityExpressionRecord,
  name: keyof ElementQuantityExpressionRecord,
  expression: string,
): boolean =>
  !hasCircularDependencies({
    ...record,
    [name]: {
      ...record[name],
      fallbackCount: createExpression(expression),
    },
  });

/**
 * Signatures for the {@link AutoUpdateSignature} dirty-check — must reflect count
 * expressions and fallback formulas, not only formatted count (DEV-3332).
 *
 * @param quantity
 * @returns
 */
const getQuantityAutoUpdateCacheSignatures = (
  quantity: ElementQuantityRecord,
): string[] => {
  const signatures: string[] = [];
  for (const name of getKeys(quantity)) {
    const row = quantity[name];
    if (!isElementQuantityExpressionProperty(row)) continue;
    if (AUTO_QUANTITY_EXCLUDED_NAMES.has(name)) continue;

    const parts: string[] = [name];
    if (hasCount(row) && isExpressionValue(row.count)) {
      parts.push(`c:${row.count.expression}`, `v:${row.count.value ?? ''}`);
    }
    if (hasFallbackCount(row) && isExpressionValue(row.fallbackCount)) {
      parts.push(`f:${row.fallbackCount.expression}`);
      // Product-sourced fallbacks (density / fill) can change resolved value without expression
      // change (DEV-3459). Every consumer of these resolved values only branches on zero vs.
      // non-zero, so track that transition instead of the exact value to avoid invalidating the
      // cache on every minor magnitude change (e.g. price-driven density updates).
      if (ALLOW_FALLBACK_REFERENCES.includes(name)) {
        const isNonZero = (row.fallbackCount.resolved ?? 0) !== 0;
        parts.push(`r:${isNonZero ? 1 : 0}`);
      }
    }
    signatures.push(parts.join(':'));
  }
  return signatures;
};

/**
 * Return priority for auto resolve (highest prio first)
 * @param prop
 * @returns
 */
const getAutoResolvePriority = (
  prop: IElementQuantityExpressionProperty,
): number => {
  if (hasCount(prop)) {
    return 1;
  }
  if (
    hasFallbackCount(prop) &&
    prop.fallbackCount.expression !== getDefaultFallbackExpression(prop)
  ) {
    return 2;
  }
  return 0;
};
